'use client'

import { useEffect, useMemo, useState } from 'react'

import { type PortableTextBlock, stegaClean } from 'next-sanity'

import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn, ptWhiteTextHeaders, ptWhiteTextParagraphs } from '@/app/utils/style'
import { fetchSvgContent } from '@/app/utils/svg'
import { generateElementId } from '@/app/utils/text'
import type { SanityImageAsset } from '@/sanity.types'

type AdvertiserReference = {
  _id: string
  _type: 'advertiser'
  companyName: string
  logo?: {
    asset?: SanityImageAsset | { _ref: string; _type: 'reference' }
    hotspot?: any
    crop?: any
  }
}

type AdvertisersSectionBlock = {
  _type: 'advertisersSection'
  _key?: string
  content?: PortableTextBlock[]
  advertisers?: AdvertiserReference[]
  logoColor?: string
  backgroundColor?: string
}

type AdvertisersProps = {
  block: AdvertisersSectionBlock
  index: number
  pageId?: string
  pageType?: string
}

type AdvertiserLogo = {
  advertiser: AdvertiserReference
  svgContent: string
}

export default function AdvertisersSection({ block, pageId, pageType }: AdvertisersProps) {
  const logoColor = stegaClean(block.logoColor) || 'text-neutral-600'
  const backgroundColor = stegaClean(block.backgroundColor) || 'content-container'
  const isDark = backgroundColor === 'content-container-dark'
  const [advertiserLogos, setAdvertiserLogos] = useState<AdvertiserLogo[]>([])

  const advertisers = useMemo(() => block.advertisers || [], [block.advertisers])

  useEffect(() => {
    const fetchLogos = async () => {
      const logos = await Promise.all(
        advertisers.map(async (advertiser) => {
          if (!advertiser?.logo) return null

          const svgContent = await fetchSvgContent(advertiser.logo, advertiser.companyName)
          return svgContent ? { advertiser, svgContent } : null
        }),
      )

      const validLogos = logos.filter(
        (logo): logo is AdvertiserLogo => logo !== null && logo.svgContent !== null,
      )
      setAdvertiserLogos(validLogos)
    }

    if (advertisers.length > 0) {
      fetchLogos()
    }
  }, [advertisers])

  const isGrid = advertiserLogos.length >= 10
  const lastRowRemainder = isGrid ? advertiserLogos.length % 5 : 0
  const lastRowFirstIndex = advertiserLogos.length - lastRowRemainder
  const mainLogos =
    lastRowRemainder > 0 ? advertiserLogos.slice(0, lastRowFirstIndex) : advertiserLogos
  const lastRowLogos = lastRowRemainder > 0 ? advertiserLogos.slice(lastRowFirstIndex) : []

  const renderLogo = (logo: AdvertiserLogo, fixedColWidth = false) => (
    <div
      key={logo.advertiser._id}
      className={cn(
        'flex items-center justify-center',
        '[&_svg]:max-h-[24px] md:[&_svg]:max-h-[32px]',
        '[&_svg]:max-w-full [&_svg]:h-auto [&_svg]:w-auto',
        '[&_svg]:mx-auto [&_svg]:my-auto',
        fixedColWidth && 'lg:w-[calc((100%-400px)/5)] lg:shrink-0',
        logoColor,
      )}
      dangerouslySetInnerHTML={{ __html: logo.svgContent }}
    />
  )

  return (
    <div
      className={cn(
        isDark
          ? 'content-container-dark py-6xl lg:py-9xl bg-green-700'
          : 'content-container my-6xl lg:my-9xl',
      )}
    >
      <div className="mx-auto max-w-[1200px]">
        {block?.content?.length && (
          <PortableText
            className={cn(
              'flex flex-col items-center justify-center text-center',
              'mx-auto mb-9xl',
              '[&_h1]:text-white-950 [&_h2]:text-white-950 [&_h3]:text-white-950 [&_h4]:text-white-950',
              '[&_p[class*="size-header-h1"]]:text-white-950 [&_p[class*="size-header-h2"]]:text-white-950 [&_p[class*="size-header-h3"]]:text-white-950 [&_p[class*="size-header-h4"]]:text-white-950',
              '[&_h5]:text-white-950 [&_h6]:text-white-950 [&_p]:text-white-950',
              '[&_p[class*="size-header-h5"]]:text-white-700 [&_p[class*="size-header-h6"]]:text-white-700',
              isDark && ptWhiteTextParagraphs,
              isDark && ptWhiteTextHeaders,
            )}
            tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
            value={block.content as PortableTextBlock[]}
            documentId={pageId}
            documentType={pageType}
            parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
            componentOverrides={{
              block: {
                h4: ({ children }) => {
                  const id = generateElementId(children)
                  return (
                    <p
                      id={id}
                      data-heading-id={id}
                      data-anchor-expose="true"
                      className="size-header-h4"
                    >
                      {children}
                    </p>
                  )
                },
                h6: ({ children }) => {
                  const id = generateElementId(children)
                  return (
                    <p
                      id={id}
                      data-heading-id={id}
                      data-anchor-expose="true"
                      className="size-header-h6 md:size-header-h6 lg:size-header-h6"
                    >
                      {children}
                    </p>
                  )
                },
              },
            }}
          />
        )}

        {advertiserLogos.length > 0 && (
          <div
            className={cn(
              'flex flex-wrap items-center justify-center gap-6xl',
              !isGrid && 'lg:justify-between',
              isGrid && 'lg:grid lg:grid-cols-5 lg:gap-x-11xl',
            )}
          >
            {mainLogos.map((logo) => renderLogo(logo))}
            {lastRowLogos.length > 0 && (
              <div className="contents lg:col-span-5 lg:flex lg:items-center lg:justify-center lg:gap-x-11xl">
                {lastRowLogos.map((logo) => renderLogo(logo, true))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
